import { useEffect, useMemo, useRef, useState } from 'react';
import type { Customer, CustomerTag, Lang } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import { TAG } from '../components/helpers';
import { useFocusTrap } from '../components/useFocusTrap';

const TAG_ORDER: CustomerTag[] = ['vip', 'regular', 'allergy'];

interface CustomerDrawerProps {
  cust: Customer | null;
  lang: Lang;
  onClose: () => void;
}

export function CustomerDrawer({ cust: snapshot, lang, onClose }: CustomerDrawerProps) {
  const { reservations: allReservations, services, dateLabel, customers } = useDashboard();
  const actions = useDashboardActions();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    phone: string;
    email: string;
    fav: string;
    noteIt: string;
  } | null>(null);

  // Same drawer-staleness fix as ResvDrawer: look the row up live from
  // the context each render so tag toggles + saved edits show
  // immediately. Fall back to snapshot if the row was deleted.
  const cust = useMemo<Customer | null>(() => {
    if (!snapshot) return null;
    return customers.find((c) => c.id === snapshot.id) ?? snapshot;
  }, [snapshot, customers]);

  const open = !!snapshot;
  useFocusTrap(drawerRef, open, onClose);

  const visits = cust ? allReservations.filter((r) => r.cust === cust.id) : [];

  // Seed the form whenever we enter edit mode.
  useEffect(() => {
    if (cust && editing) {
      setForm({
        name: cust.name,
        phone: cust.phone ?? '',
        email: cust.email ?? '',
        fav: cust.fav && cust.fav !== '—' ? cust.fav : '',
        noteIt: cust.note.it,
      });
    } else if (!editing) {
      setForm(null);
    }
  }, [editing, cust]);

  // Exit edit mode when the drawer closes.
  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  const toggleTag = async (tag: CustomerTag) => {
    if (!cust) return;
    const next = cust.tags.includes(tag)
      ? cust.tags.filter((x) => x !== tag)
      : [...cust.tags, tag];
    try {
      await actions.setCustomerTags(cust.id, next);
    } catch (err) {
      console.error('[customer-drawer] tag update failed:', err);
    }
  };

  const handleSave = async () => {
    if (!cust || !form || busy) return;
    setBusy(true);
    try {
      await actions.patchCustomer(cust.id, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        favouriteDish: form.fav.trim() || null,
        noteIt: form.noteIt.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      console.error('[customer-drawer] save failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={'scrim' + (open ? ' open' : '')}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={drawerRef}
        className={'drawer' + (open ? ' open' : '')}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label={cust ? `Customer — ${cust.name}` : 'Customer'}
        tabIndex={-1}
      >
        {cust && (
          <>
            <div className="dr-head">
              <div className="dr-top">
                <div style={{ display: 'flex', gap: 13, alignItems: 'center' }}>
                  <Avatar name={cust.name} size="lg" />
                  <div>
                    {editing && form ? (
                      <input
                        className="input"
                        style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 500, letterSpacing: '-0.02em', padding: '6px 10px', background: 'var(--panel-2)' }}
                        value={form.name}
                        onChange={(e) => setForm((f) => f && { ...f, name: e.target.value })}
                        autoFocus
                      />
                    ) : (
                      <div className="dr-name" style={{ fontSize: 20 }}>
                        {cust.name}
                      </div>
                    )}
                    <div className="dr-chips" style={{ marginTop: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                      {TAG_ORDER.map((tag) => {
                        const on = cust.tags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTag(tag)}
                            className={`chip ${on ? TAG[tag].cls : ''} chip-toggle ${on ? 'on' : ''}`}
                            aria-pressed={on}
                            title={on ? `Rimuovi ${t(TAG[tag].key, lang)}` : `Aggiungi ${t(TAG[tag].key, lang)}`}
                          >
                            {t(TAG[tag].key, lang)}
                          </button>
                        );
                      })}
                      {!editing && (
                        <button
                          type="button"
                          className="chip"
                          style={{ cursor: 'pointer', borderColor: 'var(--rule-strong)' }}
                          onClick={() => setEditing(true)}
                        >
                          <Icon name="edit" size={11} />
                          {t('edit', lang)}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="dr-close"
                  onClick={onClose}
                  aria-label={t('cancel', lang)}
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
            </div>

            <div className="dr-body">
              <div className="dr-stats">
                <div className="dr-stat">
                  <div className="v tnum">{cust.visits}</div>
                  <div className="k">{t('visits', lang)}</div>
                </div>
                <div className="dr-stat">
                  <div className="v">{cust.spend}</div>
                  <div className="k">{t('avg', lang)}</div>
                </div>
                <div className="dr-stat">
                  <div
                    className="v tnum"
                    style={{
                      fontSize: 15,
                      fontFamily: 'var(--body)',
                      fontWeight: 600,
                      paddingTop: 5,
                    }}
                  >
                    {cust.last}
                  </div>
                  <div className="k">{t('lastVisit', lang)}</div>
                </div>
              </div>

              {editing && form ? (
                <>
                  <div className="dr-field">
                    <div className="dk">{t('favourite', lang)}</div>
                    <div className="dv">
                      <input className="input" style={{ height: 30 }} value={form.fav} onChange={(e) => setForm((f) => f && { ...f, fav: e.target.value })} placeholder="—" />
                    </div>
                  </div>
                  <div className="dr-field">
                    <div className="dk">{t('phone', lang)}</div>
                    <div className="dv">
                      <input className="input tnum" style={{ height: 30 }} value={form.phone} onChange={(e) => setForm((f) => f && { ...f, phone: e.target.value })} placeholder="+39 …" />
                    </div>
                  </div>
                  <div className="dr-field">
                    <div className="dk">{t('email', lang)}</div>
                    <div className="dv">
                      <input className="input" type="email" style={{ height: 30 }} value={form.email} onChange={(e) => setForm((f) => f && { ...f, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="dr-field">
                    <div className="dk">{t('note', lang)}</div>
                    <div className="dv">
                      <textarea className="textarea" rows={3} value={form.noteIt} onChange={(e) => setForm((f) => f && { ...f, noteIt: e.target.value })} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {cust.fav && cust.fav !== '—' && (
                    <div className="dr-field">
                      <div className="dk">{t('favourite', lang)}</div>
                      <div className="dv">{cust.fav}</div>
                    </div>
                  )}
                  {cust.phone && (
                    <div className="dr-field">
                      <div className="dk">{t('phone', lang)}</div>
                      <div className="dv tnum">{cust.phone}</div>
                    </div>
                  )}
                  {cust.email && (
                    <div className="dr-field">
                      <div className="dk">{t('email', lang)}</div>
                      <div className="dv">{cust.email}</div>
                    </div>
                  )}
                  {cust.note[lang] && (
                    <div className="dr-field">
                      <div className="dk">{t('note', lang)}</div>
                      <div className="dv note">{cust.note[lang]}</div>
                    </div>
                  )}
                </>
              )}

              {visits.length > 0 && (
                <>
                  <div className="dr-section-label">{t('history', lang)}</div>
                  <div className="timeline-mini">
                    {visits.map((v) => {
                      const svc = services.find((s) => s.id === v.service);
                      return (
                        <div className="tlm" key={v.id}>
                          <div>
                            {dateLabel[lang].d} {dateLabel[lang].mon} · {v.covers}{' '}
                            {t('covers', lang)}
                          </div>
                          <div className="tlt">
                            {svc?.name[lang]} {v.start} · {t('table', lang)} {v.table}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="dr-foot">
              {editing ? (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setEditing(false)}
                    disabled={busy}
                  >
                    {t('cancel', lang)}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={busy}
                  >
                    {busy ? '…' : t('save', lang)}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-primary">
                    <Icon name="plus" size={15} />
                    {t('newResv', lang)}
                  </button>
                  <button type="button" className="btn btn-ghost">
                    <Icon name="message" size={15} />
                    {t('message', lang)}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
